import _ from 'lodash'
import { get, merge as lodashMerge } from 'lodash'
import { EMPTY, from, Observable, of, throwError } from 'rxjs'
import { first, switchMap } from 'rxjs/operators'
import localforage from 'localforage'

import { CACHE_KEYS } from './search-type-cache-key-mapping'
import { ENTITY_FIELD_TABLE_NAME, ITEM_TABLE_NAME, THESAURUS_TERM_TABLE_NAME } from './table-keys'
import { Item, SearchResponse, ThesaurusTerm, getTranslatedString, EntityField, DataSynchronisationStatus } from '@skinsoft/types'
import { itemSelectors, thesaurusTermSelectors } from '@skinsoft/selectors'

export const GLOBAL_SYNC_STATS_CACHE_KEY = 'global-sync-stats'

declare const FALLBACK_LOCALE

const offlineDataCache = localforage.createInstance({
    driver: localforage.INDEXEDDB,
    name: 'reporter-offline-data-cache',
    storeName: 'runtime_cache',
    version: 2.0,
})

export interface DataSynchronizerStats {
    maxNbToSynchronize: number,
    currentNbSychronized: number,
    lastSynchroDuration: number | null,
    lastSynchroDate: number | null,
    lastSynchroStatus: DataSynchronisationStatus | null
}

const entityFieldCache = localforage.createInstance({
    driver: localforage.INDEXEDDB,
    name: 'reporter-offline-data-cache',
    storeName: `${ENTITY_FIELD_TABLE_NAME}_runtime_cache`,
    version: 2.0,
})

/* 
 * we can't use EntityFieldDataStore because it creates circular dependencies (as OfflineDataFetcher is used in DataStore)
 * so we have to access the EntityField offlineDataFetcher manually
 * this implies that EntityField must always be cached
*/
const entityFieldHelper = {
    fetchOneOfflineMode: (id: string): Observable<EntityField> => {
        return from(entityFieldCache.getItem<EntityField>(id))
    },

    getValueFromEntityField: async (value: any, entityField: EntityField, locale: string = FALLBACK_LOCALE): Promise<string> => {
        const efPath = _.get(entityField, 'path')
        const efIsReference = _.get(entityField, 'type.referenceField')
        const efType = _.get(entityField, 'type.type')

        const rawValue = _.get(value, efPath);

        if (!efIsReference) {
            switch (efType) {
                case "String":
                    return rawValue as string;
                case "TranslatedString":
                    return getTranslatedString(rawValue, locale) as string || ""
                case "SkinDate":
                    const returnValue: number = _.get(rawValue, 'startInterval.date') as number
                    return returnValue ? returnValue.toString() : "0"
                default:
                    return ""
            }
        } else {
            switch (efType) {
                case "Item":

                    const itemCache = localforage.createInstance({
                        driver: localforage.INDEXEDDB,
                        name: 'reporter-offline-data-cache',
                        storeName: `${ITEM_TABLE_NAME}_runtime_cache`,
                        version: 2.0,
                    })

                    const item = await itemCache.getItem<Item>(rawValue);
                    return itemSelectors.title(item, locale); // Maybe improve ?
                case "ThesaurusTerm":

                    const thesaurusTermCache = localforage.createInstance({
                        driver: localforage.INDEXEDDB,
                        name: 'reporter-offline-data-cache',
                        storeName: `${THESAURUS_TERM_TABLE_NAME}_runtime_cache`,
                        version: 2.0,
                    })

                    const term = await thesaurusTermCache.getItem<ThesaurusTerm>(rawValue)
                    const returnValue = getTranslatedString(thesaurusTermSelectors.preferredTerm(term), locale) || "";
                    return returnValue;
                // TODO other entity types if needed
                default:
                    return ""
            }
        }
    }
}

export class OfflineDataFetcher<T extends { id: string }, S extends { id: string } = T> {

    private lastSynchroStats: DataSynchronizerStats = null as any;

    private cacheKey: string = "";

    private cache: LocalForage = offlineDataCache // only to avoid instanciating

    public constructor(cacheKey: string) {
        this.cacheKey = cacheKey;
        this.cache = localforage.createInstance({
            driver: localforage.INDEXEDDB,
            name: 'reporter-offline-data-cache',
            storeName: `${cacheKey}_runtime_cache`,
            version: 2.0,
        })
        this.fetchLastSynchroStats()
    }

    private get statsCacheKey() {
        return `${this.cacheKey}-stats`
    }

    public static isTemporaryId(id: string) {
        return id && typeof id === 'string' ? id.startsWith('_') : false;
    }

    public static async getLastGlobalSyncStats(): Promise<DataSynchronizerStats> {
        return await offlineDataCache.getItem(GLOBAL_SYNC_STATS_CACHE_KEY) as DataSynchronizerStats;
    }

    public static async saveGlobalSyncStats(syncStats: DataSynchronizerStats) {
        await offlineDataCache.setItem(GLOBAL_SYNC_STATS_CACHE_KEY, syncStats)
    }

    public static async isSyncError(): Promise<boolean> {
        const stats = await this.getLastGlobalSyncStats();
        if (stats) {
            return stats.lastSynchroStatus === DataSynchronisationStatus.ERROR
                || stats.lastSynchroStatus === DataSynchronisationStatus.ABORTED;
        }
        return true;
    }

    public getLastSynchroStats() {
        return this.lastSynchroStats;
    }

    public async clear() {
        const globalSynchroStats = await this.cache.getItem(GLOBAL_SYNC_STATS_CACHE_KEY)
        const entitySynchroStats = await this.cache.getItem(this.statsCacheKey)
        await this.cache.clear()
        if(entitySynchroStats) {
            await this.cache.setItem(this.statsCacheKey, entitySynchroStats)
        }
        if(globalSynchroStats) {
            await this.cache.setItem(GLOBAL_SYNC_STATS_CACHE_KEY, globalSynchroStats)
        }
    }

    public saveStats(synchroStats: DataSynchronizerStats) {
        this.cache.setItem(this.statsCacheKey, synchroStats);
        this.lastSynchroStats = synchroStats;
    }

    /**
     * Updates the value in cache having the provided id with the given entity object
     * Appends it to existing values if it doesn't exist in the cache yet
     * @param id 
     * @param entity 
     */
    public async updateOne(id: string, entity: T) {
        this.cache.setItem(id, entity)
    }

    public fetchOneFromId(id: string): Observable<T | null> {
        return from(this.cache.getItem<any>(id)).pipe(
            switchMap(item =>  {
                if(item) {
                    return of(item as T)
                } else {
                    return of(null)
                }
            }),
        )
    }

    public fetchOneFromPredicate(predicate: (data: T) => boolean): Observable<T> {
        return from(this.cache.iterate((value) => {
            if(predicate(value as T)) {
                return value
            }
        }))
        .pipe(
            switchMap(result => {
                if(result) {
                    return of(result as T)
                }

                return throwError("No data in cache for " + this.cacheKey)
            })
        )
    }

    public fetchAllFromPredicate(predicate: (data: T) => boolean): Observable<T[]> {
        let res: T[] = []
        return from(this.cache.iterate((value) => {
            if(predicate(value as T)) {
                res.push(value as T)
            }
        })).pipe(
            switchMap(() => {
                return of(res)
            })
        )
    }

    /**
     * Tries to reproduce the behaviour of online search on cache data
     * @param query 
     */
    public searchAll(query: any, locallyCreatedObjects: S[], locallyUpdatedObjects: S[], onlyOnLocallyCreated: boolean = false, language: string = FALLBACK_LOCALE): Observable<SearchResponse<S>> {
        return of(null)
        .pipe(
            switchMap(async () => {
                let result: S[] = []

                if(onlyOnLocallyCreated) {
                    result = result.concat(locallyCreatedObjects)
                } else {
                    await this.cache.iterate( (value) => {
                        const keep: boolean = true//await this.matchQuery(value as S, query)
                        if(keep) {
                            result.push(value as S)
                        }
                        return undefined
                    })
    
                    result = this.mergeUpdateCacheDataToSearchResults(result ? result : [], locallyUpdatedObjects)
                    if(locallyCreatedObjects) {
                        result = result.concat(locallyCreatedObjects)
                    }
                }

                const {reducedData, totalSize} = await this.applyFiltersAndSortsOnData(result, query, language)
                return {reducedData, totalSize}
            }),
            switchMap(
                (value: any) => {
                    return of({
                        allData: value.reducedData,
                        results: [],
                        totalSize: value.totalSize,
                    });
                }
            ),
        )
    }

    public async searchTitles(types: string[], filterText: string, pageNumber: number = 0, defaultFilter: string = "", size: number = 10, filterIds?: string[], esPath?: string, language: string = FALLBACK_LOCALE) {
        const aliasCacheKey = CACHE_KEYS['Alias']
        
        const filterPredicate = (entry: any, cacheKey: string) => {
            let match = true

            // Filter values whose 'esPath' value matches 'defaultFilter'
            if (defaultFilter && esPath) {
                const trailingRefIndex = esPath.lastIndexOf('.ref')
                let sanitizedEsPath = trailingRefIndex >= 0 ? esPath.substring(0, trailingRefIndex) : esPath // eliminating .ref from indexation if needed
                if(cacheKey.toUpperCase() === aliasCacheKey.toUpperCase()) {

                    let tags = _.get(entry, sanitizedEsPath, [])
                    if(!(tags && tags.length)) {
                        sanitizedEsPath = `alias.${sanitizedEsPath}`
                        tags = _.get(entry, sanitizedEsPath, [])
                    }

                    match = match && (tags.includes(defaultFilter) || tags.map(tag => tag?.ref).includes(defaultFilter))
                } else {
                    const valueAtEsPath = _.get(entry, esPath) ?? _.get(entry, sanitizedEsPath)
                    match = match && valueAtEsPath === defaultFilter
                }
            }

            // Filter values mathching some ids
            if (filterIds && filterIds.length > 0) {
                match = match && filterIds.includes(entry.id)
            }

            if (filterText) {
                match = match && this.titleMatcher(entry, filterText, language)
            }

            return match
        }
        
        let filteredData: any[] = []

        const cacheKeys = _.uniq(types.map(type => CACHE_KEYS[type]))
        for(const cacheKey of cacheKeys ) {
            const cache = localforage.createInstance({
                driver: localforage.INDEXEDDB,
                name: 'reporter-offline-data-cache',
                storeName: `${cacheKey}_runtime_cache`,
                version: 2.0,
            })
            
            await cache.iterate((value) => {
                if(filterPredicate(value, cacheKey)) {
                    if(cacheKey.toUpperCase() === aliasCacheKey.toUpperCase()) {
                        if((value as any).alias) {
                            filteredData.push((value as any).alias) // no idea why we get alias in some cases
                            return
                        }
                    }
                    filteredData.push(value)
                }
            })
        }

        const reshapedData = filteredData.map(
            value => {
                const reshapedValue = {
                    genericEntityTitle:
                    {
                        translatedString: {}
                    },
                    // @ts-ignore
                    id: value.id,
                }

                const title = _.get(value, `indexTitle.translatedString.${language}`) ?? 
                    _.get(value, `genericEntityTitle.translatedString.${language}`) ??
                    "..."

                _.set(
                    reshapedValue,
                    `genericEntityTitle.translatedString.${language}`,
                    title
                )
                return reshapedValue
            }
        )

        const sortedData = reshapedData
            .sort(
                (titleA, titleB) => {
                    const titleAStr = get(titleA, `genericEntityTitle.translatedString.${language}`) || "...";
                    const titleBStr = get(titleB, `genericEntityTitle.translatedString.${language}`) || "...";

                    return titleAStr.localeCompare(titleBStr, language);
                }
            )
        const clampedData = sortedData.slice(pageNumber * size, pageNumber * size + size);

        return {
            results: clampedData,
            totalSize: sortedData.length,
        };
    }

    public async append(dataToAppend: S[]) {
        for(let entity of dataToAppend) {
            await this.cache.setItem(entity.id, entity)
        }
    }

    /**
     * Override this class to customize search by entity type
     * @param entity 
     * @param queryText 
     */
    protected async queryTextFilter(entity: S, queryText: string, locale: string): Promise<boolean> {
        return true;
    }

    protected idMatcher(entity: T, id: string) {
        return _.get(entity, 'id') === id
    }

    protected titleMatcher(entity: T, title: string, locale: string) {
        const entityTitle = _.get(entity, `indexTitle.translatedString[${locale}]`, null)
        if(entityTitle) {
            return entityTitle
                .normalize('NFD')               // to ignores accentuation in matches
                .replace(/\p{Diacritic}/gu, '') // (ex: 'é' === 'e')
                .toUpperCase()
                .includes(
                    title
                    .normalize('NFD')
                    .replace(/\p{Diacritic}/gu, '')
                    .toUpperCase()
                )
        }
        return false
    }

    /**
     * Merges entities with their equivalent in cache, the correspondance is done by the 'id' field
     * @param fetchedData 
     * @param locallyUpdatedData 
     */
    private mergeUpdateCacheDataToSearchResults(fetchedData: S[], locallyUpdatedData: S[]) {
        for (let fetchedEntity of fetchedData) {
            // @ts-ignore
            const cachedUpdatedValue = locallyUpdatedData.filter(data => data.id === fetchedEntity.id)
            if (cachedUpdatedValue) {
                fetchedEntity = this.mergeCacheToEntity(fetchedEntity, cachedUpdatedValue[0])
            }
        }
        return fetchedData;
    }

    /**
     * Merges cache data with an entity to take all modifications into account
     * @param entity 
     * @param cachedValue 
     */
    private mergeCacheToEntity(entity: any, cachedValue: any) {
        const cachedCurrentState = _.get(cachedValue, 'workflowSchema.currentState')
        const mergedResult = lodashMerge(entity, cachedValue)

        if (cachedCurrentState) {
            _.set(mergedResult, 'workflowSchema.currentState', cachedCurrentState);
        }

        return mergedResult;
    }

    /**
     * Builds a predicate from a search criteria 
     * @param criteria 
     * @param entityField 
     */
    private buildPredicateFromEntityField(criteria, entityField, language: string = FALLBACK_LOCALE): (entity: any) => boolean {
        const applyOperator = (operator: string, pathToValue: string, entityValue: any): boolean => {
            switch(operator) {
                case 'is-missing':
                    return _.isNil(entityValue)
                case 'equals':
                    return entityValue === get(criteria, `value.${pathToValue}`)
                case 'contains':
                    let value = get(criteria, `value.${pathToValue}`)
                    if(typeof entityValue === 'string' || entityValue instanceof String) {
                        entityValue = entityValue.normalize('NFD').replace(/\p{Diacritic}/gu, '').toUpperCase() // to ignores accentuation in matches (ex: 'é' === 'e')
                        if(value) {
                            value = value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toUpperCase()
                        }
                    }
                    return entityValue.includes(value)
                case 'exists':
                    return !_.isNil(entityValue)
                default:
                    return false
            }
        }

        return (entity) => {
            const path = `${entityField.path}`;
            let entityValue = get(entity, `${path}.name`) || get(entity, path)
            const operator: string = get(criteria, 'linkOperator', get(criteria, 'operator'))

            if(!entityValue && operator !== 'is-missing') {
                return false
            }

            let pathToValue = ''
            switch(criteria.value.type) {
                case 'ref':
                    pathToValue = 'refId'
                    break
                case 'text':
                    pathToValue = 'text'
                    break
                /*case 'date':
                    break
                case 'path': 
                    break
                case 'bool':
                    break
                case 'search-on-ids':
                    break*/
                default:
                    if('refId' in criteria.value) {
                        pathToValue = 'refId'
                    }
                    else {
                        pathToValue = 'text'
                    }
                    break
            }

            switch (entityField?.type?.type) {
                case "LifecycleState":
                    return criteria.value.text.includes(entityValue)
                case "LinkableArchiveItem":
                    entityValue = entityValue?.linkableItem ?? entityValue?.ref // ref/referenceType is the es equivalent of linkableItem/linkableItemReferenceType for LinkableArchiveItem
                    break
                case "TranslatedString":
                    entityValue = entityValue && getTranslatedString(entityValue, language, FALLBACK_LOCALE)
                    break
                
                case "List":
                    if(criteria.value?.type === "ref") {
                        return entityValue.includes(criteria.value.refId)
                    }
                    let predicate = (value) => applyOperator(operator, pathToValue, value)
                    switch (entityField?.type?.entityFieldType?.type) {
                        case "TranslatedString":
                            predicate = translatedString => {
                                const value = translatedString && get(translatedString, ['translatedString', language], "")
                                return applyOperator(operator, pathToValue, value)
                            }
                            break
                        case "LifecycleState":
                            predicate = value => criteria.value.text.includes(value)
                            break
                        case "LinkableArchiveItem":
                            criteria = value => applyOperator(operator, pathToValue, value?.linkableItem)
                            break
                    }
                    if(operator === 'is-missing') {
                        return entityValue.every(predicate)
                    }
                    return entityValue.some(predicate)
            }

            return applyOperator(operator, pathToValue, entityValue)
        }
    }

    private async buildAdvancedSearchPredicate(linkOperator?: string, leftCriteria?, rightCriteria?, language?: string, level = 1): Promise<(entity: any) => boolean> {
        let leftPredicate = entity => true;
        let rightPredicate = entity => true;
        
        if(leftCriteria) {
            if(leftCriteria.linkOperator) {
                leftPredicate = await this.buildAdvancedSearchPredicate(leftCriteria.linkOperator, leftCriteria.leftCriteria, leftCriteria.rightCriteria, language, level+1)
            } else {
                const entityFieldId = leftCriteria.entityField;
                const entityField: EntityField = await entityFieldHelper.fetchOneOfflineMode(entityFieldId)
                    .toPromise();
                leftPredicate = this.buildPredicateFromEntityField(leftCriteria, entityField, language)
            }
        }

        if(linkOperator) {
            if(rightCriteria) {
                if(rightCriteria.linkOperator) {
                    rightPredicate = await this.buildAdvancedSearchPredicate(rightCriteria.linkOperator, rightCriteria.leftCriteria, rightCriteria.rightCriteria, language, level+1)
                } else {
                    const rightEntityFieldId = rightCriteria.entityField;
                    const rightEntityField: EntityField = await entityFieldHelper.fetchOneOfflineMode(rightEntityFieldId)
                        .toPromise();
                    rightPredicate = this.buildPredicateFromEntityField(rightCriteria, rightEntityField, language)
                }
            }

            switch (linkOperator) {
                case "AND":
                    return entity => leftPredicate(entity) && rightPredicate(entity)
                case "OR":
                    return entity => leftPredicate(entity) || rightPredicate(entity)
                case "NOT":
                    return entity => !leftPredicate(entity)
            }
        }

        return leftPredicate
    }

    private async applyFilters(data: S[], query, language: string = FALLBACK_LOCALE) {
        let filteredData = data
        // filtering on ids
        if (query.filteredIds) {
            if (filteredData.length > 0) {
                filteredData = filteredData.filter(
                    // @ts-ignore
                    item => query.filteredIds.includes(item.id)
                )
            }
        }

        //filtering excludedIds
        if(query.excludedIds) {
            if (filteredData.length > 0) {
                filteredData = filteredData.filter(
                    // @ts-ignore
                    item => !query.excludedIds.includes(item.id)
                )
            }
        }

        // Filtering on property value
        if (query.pathAndValueToCheck) {
            filteredData = filteredData.filter(item => {
                const itemValue = get(item, query.pathAndValueToCheck.pathToValue) ?? get(item, query.pathAndValueToCheck.pathToValue + ".name") 
                if(itemValue === '') {
                    return query.pathAndValueToCheck.value === ''
                }
                return query.pathAndValueToCheck.value.includes(itemValue)
            })
        }

        // Managing query text
        if (query.queryText) {
            filteredData = await Promise.all(filteredData.map(
                async value => {
                    (value as any).tempQueryTextFilter = await this.queryTextFilter(value, query.queryText, language)
                    return value
                }
            ))

            filteredData = filteredData.filter(
                value => {
                    const keep = (value as any).tempQueryTextFilter
                    delete (value as any).tempQueryTextFilter
                    return keep
                }
            )
        }

        return filteredData
    }

    private async applyAdvancedSearch(data: S[], query, language: string = FALLBACK_LOCALE) {
        // Managing advanced search
        if (query.advancedSearches && query.advancedSearches.length > 0) {
            const leftCriteria = query.advancedSearches[0].leftCriteria;
            const rightCriteria = query.advancedSearches[0].rightCriteria;
            const operator = query.advancedSearches[0].linkOperator;

            let advancedSearchPredicate = await this.buildAdvancedSearchPredicate(operator, leftCriteria, rightCriteria, language)

            const advancedSearchResult = data.filter(entity => advancedSearchPredicate(entity))
            return advancedSearchResult
        }
        return data
    }

    private async applySort(data: S[], query, language: string = FALLBACK_LOCALE) {
        // Manage sorting
        if (query.sortObject) {
            const sortEntityField = await entityFieldHelper.fetchOneOfflineMode(query.sortObject.entityFields[0])
                .pipe(first())
                .toPromise()

            // Resolving temporay sort key
            const dataWithSortingkey = await Promise.all(data.map(
                async value => {
                    if (!value) {
                        value = {} as unknown as S
                    }
                    (value as any).tempSortKey = await entityFieldHelper.getValueFromEntityField(value, sortEntityField, language) || ""
                    return value;
                }
            ))

            const sortedData = dataWithSortingkey
                .sort(
                    (value1, value2) => {
                        return query.sortObject.sortAsc ?
                            (value1 as any).tempSortKey.localeCompare((value2 as any).tempSortKey, language) :
                            (value2 as any).tempSortKey.localeCompare((value1 as any).tempSortKey, language);
                    }
                )
                .map(
                    value => {
                        delete (value as any).tempSortKey;
                        return value;
                    }
                )

            return sortedData
        }

        return data
    }

    /**
     * Applies the necessary filters and sorts to given data, according to query options
     * @param data 
     * @param query 
     */
    private async applyFiltersAndSortsOnData(data: S[], query: any, language: string = FALLBACK_LOCALE): Promise<{ reducedData: S[], totalSize: number }> {
        let res = await this.applyFilters(data, query, language)
        res = await this.applyAdvancedSearch(res, query, language)
        res = await this.applySort(res, query, language)

        const totalSize = res.length
        // Pagination options
        if (query.pageSize) {
            if (query.page) {
                const startIndex = query.page * query.pageSize;
                res = res.slice(startIndex, startIndex + query.pageSize);
            } else {
                res.splice(query.pageSize);
            }
        }

        return { reducedData: res, totalSize };
    }

    private async fetchLastSynchroStats() {
        this.lastSynchroStats = await this.cache.getItem(this.statsCacheKey) as DataSynchronizerStats
    }
}
